import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { COPAY_WAIVED_PER_DISPENSING } from "@/lib/dashboard-utils";

export function KpiBreakdownList({
  headers,
  rows,
  emptyText,
  testid,
  expandedRow,
  onToggleRow,
  renderRowDetail,
}) {
  if (!rows.length) {
    return (
      <div className="px-3 py-4 text-xs text-[#8A9C93] text-center">
        {emptyText}
      </div>
    );
  }
  const expandable = typeof onToggleRow === "function" && typeof renderRowDetail === "function";
  // Optional 4th column (rendered right after the label). Kept as an
  // opt-in via headers.length so the standard 3-column KPI cards
  // (patients, days) stay untouched. When present, the label column
  // switches from `truncate` to `whitespace-normal` — because 4-col
  // consumers (e.g. Unique Drugs w/ DIN) explicitly want the drug name
  // to WRAP, not clip, so it stays fully readable.
  // 5-column mode adds Dosage between Drug Name and DIN.
  // 6-column mode: Drug Name | Strength | Form | DIN | Patients | Rx.
  const hasCol1b = headers.length >= 4;
  const hasCol1c = headers.length >= 5;
  const hasCol1d = headers.length >= 6;
  // With 4 info columns, drug name gets squeezed on mobile — allow
  // horizontal scroll so nothing clips, and put a floor on total
  // table width so all columns stay legible.
  const wrapperClass = hasCol1d
    ? "max-h-96 overflow-auto rounded-md"
    : "max-h-96 overflow-y-auto rounded-md";
  const tableStyle = hasCol1d ? { minWidth: 460 } : undefined;
  return (
    <div data-testid={testid} className={wrapperClass}>
      <table className="w-full text-xs table-fixed" style={tableStyle}>
        <colgroup>
          <col />
          {hasCol1b ? (
            <col
              className={hasCol1d ? "w-[58px]" : hasCol1c ? "w-[68px]" : "w-[92px]"}
            />
          ) : null}
          {hasCol1c ? <col className={hasCol1d ? "w-[52px]" : "w-[88px]"} /> : null}
          {hasCol1d ? <col className="w-[78px]" /> : null}
          <col className={hasCol1d ? "w-11" : "w-14"} />
          <col className={hasCol1d ? "w-11" : "w-14"} />
        </colgroup>
        <thead className="bg-[#F7F9F8] text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold sticky top-0 z-[1]">
          <tr>
            <th className="text-left px-2 py-2 font-semibold">{headers[0]}</th>
            {hasCol1b ? (
              <th className="text-left px-2 py-2 font-semibold">{headers[1]}</th>
            ) : null}
            {hasCol1c ? (
              <th className="text-left px-2 py-2 font-semibold">{headers[2]}</th>
            ) : null}
            {hasCol1d ? (
              <th className="text-left px-2 py-2 font-semibold">{headers[3]}</th>
            ) : null}
            <th className="text-right px-2 py-2 font-semibold">
              {hasCol1d
                ? headers[4]
                : hasCol1c
                  ? headers[3]
                  : hasCol1b
                    ? headers[2]
                    : headers[1]}
            </th>
            <th className="text-right px-2 py-2 font-semibold">
              {hasCol1d
                ? headers[5]
                : hasCol1c
                  ? headers[4]
                  : hasCol1b
                    ? headers[3]
                    : headers[2]}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const isOpen = expandable && expandedRow === r.label;
            const toggle = expandable
              ? () => onToggleRow(isOpen ? null : r.label)
              : undefined;
            const rowClass = `border-b border-[#F0F4F2] align-top ${
              expandable
                ? "cursor-pointer hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                : ""
            }`;
            return (
              <React.Fragment key={`${r.label}-${idx}`}>
                <tr
                  className={rowClass}
                  data-testid={expandable ? `${testid}-row-${r.label}` : undefined}
                  onClick={toggle}
                  role={expandable ? "button" : undefined}
                  aria-expanded={expandable ? isOpen : undefined}
                >
                  <td className="px-2 py-2 text-[#1B3B2E] align-top">
                    <div className="flex items-start gap-1.5 min-w-0">
                      {expandable ? (
                        isOpen ? (
                          <ChevronDown className="h-3.5 w-3.5 text-[#5C746A] shrink-0 mt-0.5" strokeWidth={2} />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-[#5C746A] shrink-0 mt-0.5" strokeWidth={2} />
                        )
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className={hasCol1b ? "whitespace-normal break-words leading-snug" : "truncate"}>
                          {r.label}
                        </div>
                        {r.dinTag && !hasCol1b ? (
                          <span className="ml-1.5 text-[10px] font-mono-data tabular-nums text-[#5C746A] whitespace-nowrap">
                            {r.dinTag}
                          </span>
                        ) : null}
                        {r.muted ? (
                          <div className="mt-0.5 text-[10px] text-[#D36C56] font-mono-data leading-tight">
                            {r.muted}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  {hasCol1b ? (
                    <td className="px-2 py-2 font-mono-data tabular-nums text-[#5C746A] text-[11px] whitespace-nowrap align-top">
                      {r.col1b || "—"}
                    </td>
                  ) : null}
                  {hasCol1c ? (
                    <td className="px-2 py-2 font-mono-data tabular-nums text-[#5C746A] text-[11px] whitespace-nowrap align-top">
                      {r.col1c || "—"}
                    </td>
                  ) : null}
                  {hasCol1d ? (
                    <td className="px-2 py-2 font-mono-data tabular-nums text-[#5C746A] text-[11px] whitespace-nowrap align-top">
                      {r.col1d || "—"}
                    </td>
                  ) : null}
                  <td className="px-2 py-2 text-right font-mono-data text-[#1B3B2E] tabular-nums whitespace-nowrap align-top">
                    {r.col2}
                  </td>
                  <td className="px-2 py-2 text-right font-mono-data text-[#5C746A] tabular-nums whitespace-nowrap align-top">
                    {r.col3}
                  </td>
                </tr>
                {isOpen ? (
                  <tr
                    data-testid={`${testid}-detail-${r.label}`}
                    className="bg-[#F7F9F8] border-b border-[#F0F4F2]"
                  >
                    <td colSpan={hasCol1d ? 6 : hasCol1c ? 5 : hasCol1b ? 4 : 3} className="p-0">
                      {renderRowDetail(r)}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function KpiTransactionsList({ rows, testid, showFee }) {
  if (!rows.length) {
    return (
      <div className="px-3 py-4 text-xs text-[#8A9C93] text-center">
        No transactions in current view.
      </div>
    );
  }
  return (
    <div data-testid={testid} className="max-h-72 overflow-y-auto rounded-md">
      <table className="w-full text-xs">
        <thead className="bg-[#F7F9F8] text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Date</th>
            <th className="text-left px-3 py-2 font-semibold">Patient</th>
            <th className="text-left px-3 py-2 font-semibold">Drug</th>
            {showFee ? (
              <th className="text-right px-3 py-2 font-semibold">Fee</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((t, idx) => (
            <tr
              key={`${t.rx_id}-${t.service_date}-${idx}`}
              className={`border-b border-[#F0F4F2] ${
                t.fees === 0 ? "bg-[#FFF8F5]/30" : ""
              }`}
            >
              <td className="px-3 py-2 font-mono-data text-[#5C746A]">
                {t.service_date.slice(5)}
              </td>
              <td className="px-3 py-2 text-[#1B3B2E]">{t.patient}</td>
              <td className="px-3 py-2 text-[#3D4F46]">{t.drug_name}</td>
              {showFee ? (
                <td
                  className={`px-3 py-2 text-right font-mono-data ${
                    t.fees === 0 ? "text-[#D36C56]" : "text-[#1B3B2E]"
                  }`}
                >
                  {new Intl.NumberFormat("en-CA", {
                    style: "currency",
                    currency: "CAD",
                    currencyDisplay: "narrowSymbol",
                  }).format(t.fees)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function KpiCard({ label, value, sub, icon: Icon, testid, expandable, expanded, onToggle, children }) {
  const isInteractive = !!expandable;
  return (
    <div
      data-testid={testid}
      data-expanded={expanded ? "true" : "false"}
      className={`bg-white border border-[#E6EBE8] rounded-xl p-4 sm:p-6 flex flex-col gap-2 sm:gap-3 transition-all duration-200 ${
        expanded ? "border-[#2B5E4A]" : "hover:border-[#A3B8AD]"
      }`}
    >
      <button
        type="button"
        onClick={isInteractive ? onToggle : undefined}
        disabled={!isInteractive}
        className={`w-full flex flex-col gap-2 sm:gap-3 text-left ${
          isInteractive ? "cursor-pointer" : "cursor-default"
        }`}
        data-testid={`${testid}-toggle`}
        aria-expanded={expanded ? "true" : "false"}
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] sm:text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
            {label}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isInteractive ? (
              <ChevronDown
                className={`h-4 w-4 text-[#5C746A] transition-transform duration-200 ${
                  expanded ? "rotate-180 text-[#2B5E4A]" : ""
                }`}
                strokeWidth={1.75}
              />
            ) : null}
            <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-[#F0F4F2] flex items-center justify-center">
              <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#2B5E4A]" strokeWidth={1.75} />
            </div>
          </div>
        </div>
        <div className="font-mono-data text-2xl sm:text-[2.75rem] font-light tracking-tighter text-[#1B3B2E] leading-none break-words">
          {value}
        </div>
        {sub ? (
          <div className="text-[11px] sm:text-xs text-[#5C746A]">{sub}</div>
        ) : null}
      </button>
      {expanded && children ? (
        <div
          data-testid={`${testid}-details`}
          className="mt-3 pt-3 border-t border-[#F0F4F2] -mx-1 sm:-mx-2"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}


const currencyFmt = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  currencyDisplay: "narrowSymbol",
});

export function PatientRxDetail({ patient, transactions }) {
  const rows = (transactions || [])
    .filter((t) => t.patient === patient)
    .sort((a, b) => {
      if (a.service_date !== b.service_date) {
        return a.service_date.localeCompare(b.service_date);
      }
      return (a.drug_name || "").localeCompare(b.drug_name || "");
    });

  if (!rows.length) {
    return (
      <div className="px-3 py-3 text-[11px] text-[#8A9C93] text-center">
        No prescription detail available for this patient in the current view.
      </div>
    );
  }

  let feeTotal = 0;
  let markupTotal = 0;
  let p1Total = 0;
  let p2Total = 0;
  for (const r of rows) {
    feeTotal += r.fees || 0;
    markupTotal += r.markup || 0;
    p1Total += r.plan1_paid || 0;
    p2Total += r.plan2_paid || 0;
  }
  const hasMarkup = markupTotal > 0.005;
  const hasPlan2 = p2Total > 0.005;

  return (
    <div className="overflow-x-auto" data-testid={`patient-rx-detail-${patient}`}>
      <table className="w-full text-[11px]">
        <thead className="text-[9px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
          <tr>
            <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Date</th>
            <th className="text-left px-3 py-2 font-semibold">Drug</th>
            <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Fee</th>
            {hasMarkup ? (
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Markup</th>
            ) : null}
            <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">
              {hasPlan2 ? "Plan 1" : "Plan Paid"}
            </th>
            {hasPlan2 ? (
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Plan 2</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={`${r.rx_id || r.drug_name}-${r.service_date}-${idx}`}
              className={`border-t border-[#E6EBE8] text-[#3D4F46] ${
                (r.fees || 0) === 0 ? "bg-[#FFF8F5]/40" : ""
              }`}
            >
              <td className="px-3 py-2 font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                {r.service_date}
              </td>
              <td className="px-3 py-2 text-[#1B3B2E]">{r.drug_name}</td>
              <td
                className={`px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap ${
                  (r.fees || 0) === 0 ? "text-[#D36C56]" : "text-[#1B3B2E]"
                }`}
              >
                {currencyFmt.format(r.fees || 0)}
              </td>
              {hasMarkup ? (
                <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#3D4F46]">
                  {currencyFmt.format(r.markup || 0)}
                </td>
              ) : null}
              <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#3D4F46]">
                {currencyFmt.format(r.plan1_paid || 0)}
              </td>
              {hasPlan2 ? (
                <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#3D4F46]">
                  {currencyFmt.format(r.plan2_paid || 0)}
                </td>
              ) : null}
            </tr>
          ))}
          <tr className="border-t border-[#E6EBE8] bg-white text-[#1B3B2E] font-semibold">
            <td className="px-3 py-2" colSpan={2}>
              Subtotal · {rows.length} Rx
            </td>
            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
              {currencyFmt.format(feeTotal)}
            </td>
            {hasMarkup ? (
              <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                {currencyFmt.format(markupTotal)}
              </td>
            ) : null}
            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
              {currencyFmt.format(p1Total)}
            </td>
            {hasPlan2 ? (
              <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                {currencyFmt.format(p2Total)}
              </td>
            ) : null}
          </tr>
        </tbody>
      </table>
    </div>
  );
}


export function DailyClaimsDetail({ serviceDate, transactions }) {
  const [openPatient, setOpenPatient] = useState(null);
  const rows = (transactions || []).filter((t) => t.service_date === serviceDate);
  if (!rows.length) {
    return (
      <div className="px-4 py-3 text-[11px] text-[#8A9C93] text-center">
        No transactions for this day.
      </div>
    );
  }
  // Small helper: qty may be an integer (14 tabs) or a fraction (0.5 tabs).
  // Trim trailing `.0` so 14.0 reads as "14" but 0.5 stays "0.5".
  const formatQty = (q) => {
    const n = Number(q) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
  };
  // Unit price = drug cost ÷ qty. Uses up to 4 decimal places for
  // sub-dollar-per-unit drugs (e.g. $0.0435/tab) but trims trailing
  // zeros so common prices ($3.04) don't read as $3.0400.
  const formatUnitPrice = (v) => {
    if (!isFinite(v)) return "—";
    const n = Number(v) || 0;
    const s = n.toFixed(4).replace(/\.?0+$/, "");
    return `$${s || "0"}`;
  };
  // Group by patient → claim list. `total` shows up in the collapsed
  // patient header pill and is what ODB DEPOSITED for the patient's
  // scripts on this day (submitted total minus $2 co-pay waived per
  // paid dispensing). Matches the raw pharmacy report's paid figure
  // exactly, so the subtotal reconciles with the source PDF.
  const map = new Map();
  for (const t of rows) {
    const key = t.patient || "(unknown)";
    const slot = map.get(key) || { patient: key, claims: [], total: 0, zero: 0 };
    slot.claims.push(t);
    const copay =
      (t.fees || 0) > 0 ? COPAY_WAIVED_PER_DISPENSING : 0;
    const planPaid = (t.plan1_paid || 0) + (t.plan2_paid || 0);
    // ODB paid = submitted total − copay waived. planPaid is the raw
    // submitted amount (pre-waiver) per the parser's column mapping.
    slot.total += Math.max(0, planPaid - copay);
    if ((t.fees || 0) === 0) slot.zero += 1;
    map.set(key, slot);
  }
  const groups = Array.from(map.values()).sort((a, b) =>
    a.patient.localeCompare(b.patient)
  );

  return (
    <div data-testid={`daily-claims-${serviceDate}`} className="p-2 space-y-1.5">
      {groups.map((g, gi) => {
        const isOpen = openPatient === g.patient;
        // $2 waived copay applies to every paid dispensing (fee > 0). The
        // pharmacy never collects that $2 — used for the Copay Waived column
        // display and to derive net fees for the Net Paid roll-up.
        const copayFor = (c) =>
          (c.fees || 0) > 0 ? COPAY_WAIVED_PER_DISPENSING : 0;
        // Drug cost = plan-paid minus GROSS fees minus markup. `plan1_paid`
        // in our transaction store is the SUBMITTED total (the raw pharmacy
        // PDF's Plan-1 column carries the pre-waiver amount, before the $2
        // patient co-pay is deducted by ODB). So the identity that recovers
        // ingredient cost is:
        //   plan_paid_submitted = drug_cost + gross_fee + markup
        // Aaron Brown Kadian sanity: 55.08 − 8.83 − 3.62 = 42.63 ✓.
        const drugCostFor = (c) => {
          const planPaid = (c.plan1_paid || 0) + (c.plan2_paid || 0);
          return Math.max(
            0,
            planPaid - (c.fees || 0) - (c.markup || 0)
          );
        };
        let feeTotal = 0;
        let markupTotal = 0;
        let copayTotal = 0;
        let qtyTotal = 0;
        for (const c of g.claims) {
          feeTotal += c.fees || 0;
          markupTotal += c.markup || 0;
          copayTotal += copayFor(c);
          qtyTotal += c.qty || 0;
        }
        let drugCostTotal = 0;
        for (const c of g.claims) drugCostTotal += drugCostFor(c);
        return (
          <div
            key={`${g.patient}-${gi}`}
            className="bg-white border border-[#E6EBE8] rounded-lg overflow-hidden"
            data-testid={`daily-patient-${gi}`}
          >
            <button
              type="button"
              onClick={() => setOpenPatient(isOpen ? null : g.patient)}
              aria-expanded={isOpen}
              data-testid={`daily-patient-toggle-${gi}`}
              className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 text-left transition-colors ${
                isOpen
                  ? "bg-[#F0F4F2] border-b border-[#E6EBE8]"
                  : "bg-white hover:bg-[#F7F9F8] active:bg-[#F0F4F2]"
              }`}
            >
              <div className="min-w-0 flex items-center gap-1.5">
                {isOpen ? (
                  <ChevronDown
                    className="h-3.5 w-3.5 text-[#5C746A] shrink-0"
                    strokeWidth={2}
                  />
                ) : (
                  <ChevronRight
                    className="h-3.5 w-3.5 text-[#5C746A] shrink-0"
                    strokeWidth={2}
                  />
                )}
                <div className="text-[12px] font-semibold text-[#1B3B2E] truncate">
                  {g.patient}
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-[#5C746A] font-mono-data tabular-nums whitespace-nowrap shrink-0">
                <span>
                  {g.claims.length} Rx
                  {g.zero > 0 ? (
                    <span className="text-[#D36C56] ml-1">· {g.zero} zero</span>
                  ) : null}
                </span>
                <span
                  className={`font-semibold ${
                    g.total === 0 ? "text-[#D36C56]" : "text-[#1B3B2E]"
                  }`}
                >
                  {currencyFmt.format(g.total || 0)}
                </span>
              </div>
            </button>
            {isOpen ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] border-separate border-spacing-0">
                  <thead className="text-[9px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                    <tr>
                      <th className="text-left px-3 py-1 font-semibold whitespace-nowrap">Time</th>
                      <th className="text-left px-3 py-1 font-semibold">Drug</th>
                      {/* --- Group A: Drug + Markup --- */}
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap border-l border-[#E6EBE8]">Qty</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Unit $</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Drug Cost</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Markup</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Reimbursed</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">%</th>
                      {/* --- Group B: Dispensing fee --- */}
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap border-l border-[#E6EBE8]">Fee</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Copay Waived</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Reimbursed</th>
                      {/* --- Group C: Totals --- */}
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap border-l border-[#E6EBE8]">Submitted</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">Paid</th>
                      <th className="text-right px-3 py-1 font-semibold whitespace-nowrap">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.claims
                      .slice()
                      .sort((a, b) => (a.time || "").localeCompare(b.time || ""))
                      .map((c, ci) => {
                        // Row-level reconciliation math. Three visual groups:
                        //   A) Drug + Markup: dc, mk, dc+mk, markup%
                        //   B) Dispensing fee: fee (gross), copay waived, fee net
                        //   C) Totals: submitted (A+B gross), paid (submitted−copay), net margin %
                        const dc = drugCostFor(c);
                        const mk = c.markup || 0;
                        const feeGross = c.fees || 0;
                        const cw = copayFor(c);
                        const reimburseA = dc + mk;
                        const markupPct = dc > 0 ? (mk / dc) * 100 : null;
                        const feeNet = Math.max(0, feeGross - cw);
                        const submitted = reimburseA + feeGross;
                        const paid = Math.max(0, submitted - cw);
                        const pharmacyNet = mk + feeNet;
                        const netMarginPct = paid > 0 ? (pharmacyNet / paid) * 100 : null;
                        // Reimbursement quality indicators.
                        //  A: green when the drug+markup portion actually delivered
                        //     $ back; red when it didn't (compound/zero-value row).
                        //  B: green when the fee came back cleanly (fee > 0 AND
                        //     only the standard $2 co-pay — or less — was
                        //     deducted); red otherwise (no fee received or an
                        //     unexpected deduction beyond the copay).
                        const reimburseAOk = reimburseA > 0.005;
                        const reimburseBOk =
                          feeGross > 0.005 && cw <= 2.01 && feeNet > 0.005;
                        return (
                          <tr
                            key={`${c.rx_id || c.drug_name}-${ci}`}
                            className={`text-[#3D4F46] ${
                              (c.fees || 0) === 0 ? "bg-[#FFF8F5]/40" : ""
                            }`}
                          >
                            <td className="border-t border-[#E6EBE8] px-3 py-1 font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                              {c.time || "—"}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-[#1B3B2E]">
                              {c.drug_name}
                              {c.din ? (
                                <span className="ml-1.5 text-[9px] font-mono-data tabular-nums text-[#5C746A] whitespace-nowrap">
                                  DIN {c.din}
                                </span>
                              ) : null}
                            </td>
                            {/* Group A */}
                            <td className="border-t border-l border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                              {c.qty ? formatQty(c.qty) : "—"}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                              {c.qty > 0 ? formatUnitPrice(dc / c.qty) : "—"}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#3D4F46]">
                              {currencyFmt.format(dc)}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#3D4F46]">
                              {currencyFmt.format(mk)}
                            </td>
                            <td className={`border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap font-medium ${
                              reimburseAOk ? "text-[#2E5C46]" : "text-[#D36C56]"
                            }`}>
                              {currencyFmt.format(reimburseA)}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                              {markupPct == null ? "—" : `${markupPct.toFixed(1)}%`}
                            </td>
                            {/* Group B */}
                            <td
                              className={`border-t border-l border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap ${
                                feeGross === 0 ? "text-[#D36C56]" : "text-[#3D4F46]"
                              }`}
                            >
                              {currencyFmt.format(feeGross)}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#D36C56]">
                              {cw > 0 ? `−${currencyFmt.format(cw)}` : "—"}
                            </td>
                            <td className={`border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap font-medium ${
                              reimburseBOk ? "text-[#2E5C46]" : "text-[#D36C56]"
                            }`}>
                              {currencyFmt.format(feeNet)}
                            </td>
                            {/* Group C */}
                            <td className="border-t border-l border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#3D4F46]">
                              {currencyFmt.format(submitted)}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#1B3B2E] font-semibold">
                              {currencyFmt.format(paid)}
                            </td>
                            <td className="border-t border-[#E6EBE8] px-3 py-1 text-right font-mono-data tabular-nums whitespace-nowrap text-[#2E5C46] font-semibold">
                              {netMarginPct == null ? "—" : `${netMarginPct.toFixed(1)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    {(() => {
                      // Subtotals across the group's claims — mirror the row math.
                      const reimburseATotal = drugCostTotal + markupTotal;
                      const markupPctTotal =
                        drugCostTotal > 0 ? (markupTotal / drugCostTotal) * 100 : null;
                      const feeNetTotal = Math.max(0, feeTotal - copayTotal);
                      const submittedTotal = drugCostTotal + markupTotal + feeTotal;
                      const paidTotal = Math.max(0, submittedTotal - copayTotal);
                      const netTotal = markupTotal + feeNetTotal;
                      const netMarginTotal =
                        paidTotal > 0 ? (netTotal / paidTotal) * 100 : null;
                      // Same green/red logic as the row indicators, applied
                      // to the roll-up: A is green when the aggregate drug+
                      // markup portion delivered $ back; B is green when
                      // fees came back with only the standard $2/dispensing
                      // co-pay deducted (or less).
                      const reimburseAOk = reimburseATotal > 0.005;
                      const feeCountPaid = g.claims.filter(
                        (c) => (c.fees || 0) > 0
                      ).length;
                      const reimburseBOk =
                        feeTotal > 0.005 &&
                        feeNetTotal > 0.005 &&
                        copayTotal <= (feeCountPaid * 2 + 0.01);
                      return (
                        <tr className="bg-[#F7F9F8] text-[#1B3B2E] font-semibold">
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5" colSpan={2}>
                            Subtotal · {g.claims.length} Rx
                          </td>
                          {/* Group A */}
                          <td className="border-t border-l border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                            {qtyTotal > 0 ? formatQty(qtyTotal) : "—"}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                            {qtyTotal > 0 ? formatUnitPrice(drugCostTotal / qtyTotal) : "—"}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap">
                            {currencyFmt.format(drugCostTotal)}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap">
                            {currencyFmt.format(markupTotal)}
                          </td>
                          <td className={`border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap ${
                            reimburseAOk ? "text-[#2E5C46]" : "text-[#D36C56]"
                          }`}>
                            {currencyFmt.format(reimburseATotal)}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                            {markupPctTotal == null ? "—" : `${markupPctTotal.toFixed(1)}%`}
                          </td>
                          {/* Group B */}
                          <td className="border-t border-l border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap">
                            {currencyFmt.format(feeTotal)}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#D36C56]">
                            {copayTotal > 0 ? `−${currencyFmt.format(copayTotal)}` : "—"}
                          </td>
                          <td className={`border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap ${
                            reimburseBOk ? "text-[#2E5C46]" : "text-[#D36C56]"
                          }`}>
                            {currencyFmt.format(feeNetTotal)}
                          </td>
                          {/* Group C */}
                          <td className="border-t border-l border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap">
                            {currencyFmt.format(submittedTotal)}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap">
                            {currencyFmt.format(paidTotal)}
                          </td>
                          <td className="border-t border-[#E6EBE8] px-3 py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#2E5C46]">
                            {netMarginTotal == null ? "—" : `${netMarginTotal.toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
